import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import './App.css';

const getNotificationMessages = (locationParam) => [
  { text: 'Sarah just saved $2,400/year on energy bills', icon: 'fa-piggy-bank' },
  { text: 'Only 5 spots left this week', icon: 'fa-clock' },
  { text: `Mike from ${locationParam} cut his bills by 40%`, icon: 'fa-circle-check' },
  { text: '3 spots remaining for next week', icon: 'fa-calendar' },
  { text: 'Jennifer saved $300/month after upgrade', icon: 'fa-house' },
  { text: 'Limited availability - 2 spots left', icon: 'fa-fire' }
];

const heroSlides = [
  {
    image: '/images/hero-1.jpg',
    title: 'Cut Your Energy Bills by Up to 40%',
    subtitle: 'Professional insulation upgrades that pay for themselves'
  },
  {
    image: '/images/hero-2.jpg',
    title: 'Stop Paying to Heat the Outdoors',
    subtitle: 'Modern insulation keeps your money where it belongs - in your pocket'
  },
  {
    image: '/images/hero-3.jpg',
    title: 'ROI in Under 2 Years',
    subtitle: 'Most homeowners see complete payback within 18-24 months'
  }
];

function SavingsPage() {
  const [searchParams] = useSearchParams();
  const locationParam = searchParams.get('location') || 'Denver';
  
  // Load saved form data from localStorage on mount
  const loadSavedFormData = () => {
    try {
      const saved = localStorage.getItem('levelup_form_data');
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved form data:', e);
    }
    return {
      name: '',
      email: '',
      phone: '',
      address: '',
      issue: ''
    };
  };

  const [formData, setFormData] = useState(loadSavedFormData);
  const [submitted, setSubmitted] = useState(false);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [lastSaved, setLastSaved] = useState(null);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const notificationMessages = getNotificationMessages(locationParam);
    
    const showRandomNotification = () => {
      const randomMessage = notificationMessages[Math.floor(Math.random() * notificationMessages.length)];
      setNotificationMessage(randomMessage);
      setShowNotification(true);
      
      setTimeout(() => {
        setShowNotification(false);
      }, 5000);
    };

    const initialTimer = setTimeout(() => {
      showRandomNotification();
    }, 5000);

    const interval = setInterval(() => {
      showRandomNotification();
    }, 30000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationParam]);

  const saveFormData = async (data, isPartial = true) => {
    try {
      localStorage.setItem('levelup_form_data', JSON.stringify(data));
      
      try {
        const apiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
        const response = await fetch(`${apiUrl}/api/save-form`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...data,
            location: locationParam,
            timestamp: new Date().toISOString(),
            isPartial: isPartial,
            url: window.location.href,
            page: 'savings'
          })
        });
        
        if (response.ok) {
          setLastSaved(new Date());
        }
      } catch (backendError) {
        console.log('Backend save not available:', backendError);
      }
    } catch (error) {
      console.error('Error saving form data:', error);
    }
  };

  const handleChange = (e) => {
    const newFormData = {
      ...formData,
      [e.target.name]: e.target.value
    };
    setFormData(newFormData);
    
    clearTimeout(window.formSaveTimeout);
    window.formSaveTimeout = setTimeout(() => {
      saveFormData(newFormData, true);
    }, 1000);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    await saveFormData(formData, false);
    localStorage.removeItem('levelup_form_data');
    
    try {
      const apiUrl = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:3001');
      const response = await fetch(`${apiUrl}/api/submit-form`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...formData,
          location: locationParam,
          timestamp: new Date().toISOString(),
          isPartial: false,
          url: window.location.href,
          page: 'savings'
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        console.log('Form submitted successfully');
      } else {
        throw new Error('Submission failed');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitted(true);
      
      localStorage.setItem('levelup_pending_submissions', JSON.stringify([
        ...(JSON.parse(localStorage.getItem('levelup_pending_submissions') || '[]')),
        {
          ...formData,
          location: locationParam,
          timestamp: new Date().toISOString(),
          page: 'savings'
        }
      ]));
    }
  };

  return (
    <div className="App">
      {/* Header with Logo */}
      <header className="site-header">
        <div className="container">
          <div className="header-content">
            <img 
              src="/images/logo.png" 
              alt="Level Up Insulation" 
              className="logo"
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
            <div className="header-location">
              Serving {locationParam}
            </div>
            <a href="tel:+17204384271" className="btn header-call-btn">
              <i className="fa-solid fa-phone"></i> Call
            </a>
            <a href="#form" className="btn btn-primary header-cta-btn">
              Free Estimate
            </a>
          </div>
        </div>
      </header>

      {/* Notification Popup */}
      {showNotification && (
        <div className="notification-popup">
          <div className="notification-content">
            <span className="notification-icon"><i className={`fa-solid ${notificationMessage.icon}`}></i></span>
            <span className="notification-text">{notificationMessage.text}</span>
            <button 
              className="notification-close"
              onClick={() => setShowNotification(false)}
              aria-label="Close notification"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Hero Slider Section */}
      <section className="hero-slider">
        <div className="hero-slides">
          {heroSlides.map((slide, index) => (
            <div
              key={index}
              className={`hero-slide ${index === currentSlide ? 'active' : ''}`}
              style={{ backgroundImage: `url(${slide.image})` }}
            >
              <div className="hero-overlay"></div>
            </div>
          ))}
        </div>
        <div className="hero-content">
          <div className="container">
            <div className="badge">SAVE UP TO 40% ON ENERGY BILLS</div>
            <h1 className="hero-title">
              <span className="hero-title-thin">Stop Wasting Money</span> on High Energy Bills in {locationParam}
            </h1>
            <p className="hero-subtitle">
              {locationParam} homeowners are saving $200-$400/month with proper insulation. 
              Free energy savings assessment. See exactly how much YOU could save.
            </p>
            
            {/* Hero Savings Calculator */}
            <div className="hero-savings-calculator">
              <div className="hero-calc-header">
                <span className="hero-calc-icon"><i className="fa-solid fa-piggy-bank"></i></span>
                <span className="hero-calc-title">Average Monthly Savings</span>
              </div>
              <div className="hero-calc-bar-wrapper">
                <div className="hero-calc-bar">
                  <div className="hero-calc-fill" style={{ width: '75%' }}></div>
                </div>
                <div className="hero-calc-markers">
                  <span>$0</span>
                  <span>$150</span>
                  <span>$300</span>
                  <span>$450</span>
                </div>
              </div>
              <div className="hero-calc-result">
                <span className="hero-calc-amount">$300+</span>
                <span className="hero-calc-label">per month</span>
              </div>
              <p className="hero-calc-subtext">Based on {locationParam} homeowner averages</p>
            </div>

            {/* Early Trust Signals */}
            <div className="hero-trust-bar">
              <div className="trust-item">
                <span className="trust-icon"><i className="fa-solid fa-star"></i></span>
                <span>4.9/5 from 312+ Reviews</span>
              </div>
              <div className="trust-item">
                <span className="trust-icon"><i className="fa-solid fa-location-dot"></i></span>
                <span>Local {locationParam} Team</span>
              </div>
              <div className="trust-item">
                <span className="trust-icon"><i className="fa-solid fa-shield-halved"></i></span>
                <span>Savings Guaranteed</span>
              </div>
            </div>
            
            <div className="hero-cta">
              <a href="#form" className="btn btn-primary btn-emotional">
                Yes! Show Me My Savings →
              </a>
              <p className="hero-guarantee">✓ Free assessment • ✓ No obligation • ✓ See your savings estimate</p>
            </div>
            <div className="hero-stats">
              <div className="stat">
                <div className="stat-number">$300+</div>
                <div className="stat-label">Avg. Monthly Savings</div>
              </div>
              <div className="stat">
                <div className="stat-number">40%</div>
                <div className="stat-label">Energy Bill Reduction</div>
              </div>
              <div className="stat">
                <div className="stat-number">18 mo</div>
                <div className="stat-label">Average Payback</div>
              </div>
            </div>
          </div>
        </div>
        <div className="hero-dots">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              className={`dot ${index === currentSlide ? 'active' : ''}`}
              onClick={() => setCurrentSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            ></button>
          ))}
        </div>
      </section>

      {/* Social Proof Section */}
      <section className="social-proof">
        <div className="container">
          <div className="social-proof-content">
            <div className="stars">
              <span className="star"><i className="fa-solid fa-star"></i></span>
              <span className="star"><i className="fa-solid fa-star"></i></span>
              <span className="star"><i className="fa-solid fa-star"></i></span>
              <span className="star"><i className="fa-solid fa-star"></i></span>
              <span className="star"><i className="fa-solid fa-star"></i></span>
            </div>
            <p className="social-proof-text">
              <strong>$2.3M+</strong> saved for <strong>312+</strong> homeowners in {locationParam}
            </p>
          </div>
        </div>
      </section>

      {/* Pain Points Section - Money Focus */}
      <section 
        className="pain-points problem"
        style={{
          backgroundImage: 'url(/images/work-1.jpg)'
        }}
      >
        <div className="shape-bottom"></div>
        <div className="container">
          <h2 className="section-title">Are High Energy Bills Draining Your Wallet?</h2>
          <p className="section-subtitle">
            If your energy bills keep climbing, your home is literally leaking money. Here's what's happening:
          </p>
          <div className="pain-grid">
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-money-bill-wave"></i></div>
              <h3>$200-$500/Month Wasted</h3>
              <p>Poor insulation means you're paying to heat and cool the outdoors. Money straight out the window</p>
            </div>
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-chart-line"></i></div>
              <h3>Bills Keep Rising</h3>
              <p>Energy costs increase 3-5% yearly. Bad insulation makes it worse every single year</p>
            </div>
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-fan"></i></div>
              <h3>HVAC Working Overtime</h3>
              <p>Your heating and cooling system runs constantly, wearing out faster and costing more to repair</p>
            </div>
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-temperature-low"></i></div>
              <h3>Never Comfortable</h3>
              <p>Despite high bills, your home still has cold spots, drafts, and inconsistent temperatures</p>
            </div>
            <div className="pain-item">
              <div className="pain-icon"><i className="fa-solid fa-scale-unbalanced"></i></div>
              <h3>Frustrating Tradeoffs</h3>
              <p>You shouldn't have to choose between comfort and affordable bills. Proper insulation gives you both</p>
            </div>
          </div>
          <div className="pain-cta">
            <p className="pain-message">
              <strong>Every month you wait, you're losing money.</strong> 
              Proper insulation typically pays for itself in 18-24 months, then it's pure savings.
            </p>
            <a href="#form" className="btn btn-primary btn-emotional">
              Calculate My Savings →
            </a>
          </div>
        </div>
      </section>

      {/* Savings Calculator Section */}
      <section className="savings-calculator">
        <div className="container">
          <h2 className="section-title">How Much Could You Save?</h2>
          <p className="section-subtitle-light">
            See what {locationParam} homeowners like you are saving every month
          </p>
          <div className="savings-grid">
            <div className="savings-card">
              <div className="savings-icon"><i className="fa-solid fa-house"></i></div>
              <h3>Small Home</h3>
              <p className="savings-sqft">Under 1,500 sq ft</p>
              <div className="savings-amount">$150-$250</div>
              <p className="savings-period">per month</p>
              <p className="savings-annual">$1,800-$3,000/year</p>
            </div>
            <div className="savings-card savings-popular">
              <div className="savings-badge">Most Common</div>
              <div className="savings-icon"><i className="fa-solid fa-house-chimney"></i></div>
              <h3>Medium Home</h3>
              <p className="savings-sqft">1,500-2,500 sq ft</p>
              <div className="savings-amount">$250-$400</div>
              <p className="savings-period">per month</p>
              <p className="savings-annual">$3,000-$4,800/year</p>
            </div>
            <div className="savings-card">
              <div className="savings-icon"><i className="fa-solid fa-building"></i></div>
              <h3>Large Home</h3>
              <p className="savings-sqft">2,500+ sq ft</p>
              <div className="savings-amount">$400-$600</div>
              <p className="savings-period">per month</p>
              <p className="savings-annual">$4,800-$7,200/year</p>
            </div>
          </div>
          <div className="savings-cta">
            <p>Want to know YOUR exact savings potential?</p>
            <a href="#form" className="btn btn-primary btn-emotional">
              Get My Free Savings Estimate →
            </a>
          </div>
        </div>
      </section>

      {/* ROI Section */}
      <section className="roi-section">
        <div className="container">
          <h2 className="section-title">The Numbers Don't Lie</h2>
          <div className="roi-grid">
            <div className="roi-item">
              <div className="roi-number">18-24</div>
              <div className="roi-label">Months to Payback</div>
              <p>Most homeowners see complete ROI within 2 years</p>
            </div>
            <div className="roi-item">
              <div className="roi-number">15-25</div>
              <div className="roi-label">Years of Savings</div>
              <p>Quality insulation lasts decades after payback</p>
            </div>
            <div className="roi-item">
              <div className="roi-number">$50K+</div>
              <div className="roi-label">Lifetime Savings</div>
              <p>Average savings over the life of your insulation</p>
            </div>
            <div className="roi-item">
              <div className="roi-number">5-10%</div>
              <div className="roi-label">Home Value Increase</div>
              <p>Energy-efficient homes sell faster and for more</p>
            </div>
          </div>
        </div>
      </section>

      {/* Animated Work Showcase Section */}
      <section className="work-showcase">
        <div className="container">
          <h2 className="section-title">How We Maximize Your Savings</h2>
          <p className="section-subtitle">
            Our proven process identifies every opportunity to reduce your energy costs
          </p>
          <div className="showcase-grid">
            <div className="showcase-item">
              <div className="showcase-image-wrapper">
                <img 
                  src="/images/work-1.jpg" 
                  alt="Energy audit" 
                  className="showcase-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="showcase-content">
                <h3>Energy Loss Assessment</h3>
                <p>We find exactly where your home is losing energy and calculate your savings potential</p>
              </div>
            </div>
            <div className="showcase-item">
              <div className="showcase-image-wrapper">
                <img 
                  src="/images/work-2.jpg" 
                  alt="Custom solution" 
                  className="showcase-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="showcase-content">
                <h3>Custom Savings Plan</h3>
                <p>Tailored recommendations based on your home, budget, and savings goals</p>
              </div>
            </div>
            <div className="showcase-item">
              <div className="showcase-image-wrapper">
                <img 
                  src="/images/work-3.jpg" 
                  alt="Professional installation" 
                  className="showcase-image"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
              <div className="showcase-content">
                <h3>Expert Installation</h3>
                <p>Proper installation maximizes performance. Poorly installed insulation wastes your investment</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <div className="section-divider"></div>
      <section className="solution">
        <div className="container">
          <h2 className="section-title">Your Path to Lower Bills</h2>
          <p className="section-subtitle">
            From free assessment to monthly savings. Here's how we help you keep more money in your pocket
          </p>
          <div className="solution-wrapper">
            <div className="solution-grid">
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-2.jpg" alt="Free assessment" className="solution-image" />
                <div className="solution-number">01</div>
              </div>
              <div className="solution-content">
                <h3>Free Savings Assessment</h3>
                <p>We calculate exactly how much you're losing and how much you could save</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-4.jpg" alt="Custom plan" className="solution-image" />
                <div className="solution-number">02</div>
              </div>
              <div className="solution-content">
                <h3>Custom Savings Plan</h3>
                <p>Options for every budget with clear ROI timelines for each</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-3.jpg" alt="Professional installation" className="solution-image" />
                <div className="solution-number">03</div>
              </div>
              <div className="solution-content">
                <h3>Professional Installation</h3>
                <p>Expert installation in 1-2 days with minimal disruption to your life</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-1.jpg" alt="Start saving" className="solution-image" />
                <div className="solution-number">04</div>
              </div>
              <div className="solution-content">
                <h3>Start Saving Immediately</h3>
                <p>See lower bills starting with your very first month after installation</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-5.jpg" alt="Payback" className="solution-image" />
                <div className="solution-number">05</div>
              </div>
              <div className="solution-content">
                <h3>Full Payback in 18-24 Months</h3>
                <p>Your investment pays for itself, then keeps saving you money for decades</p>
              </div>
            </div>
            <div className="solution-card">
              <div className="solution-image-wrapper">
                <img src="/images/work-6.jpg" alt="Lifetime savings" className="solution-image" />
                <div className="solution-number">06</div>
              </div>
              <div className="solution-content">
                <h3>$50K+ Lifetime Savings</h3>
                <p>Quality insulation lasts 15-25 years. That's pure profit after payback</p>
              </div>
            </div>
            </div>
            <div className="solution-customer-image">
              <img 
                src="/images/customer-1.jpg" 
                alt="Happy customer" 
                className="customer-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <div className="customer-name">
                <p className="customer-name-text">Joanne Roarke</p>
                <p className="customer-label">Saving $340/month</p>
              </div>
              <a href="#form" className="btn btn-primary customer-cta-btn">
                Get Your Free Estimate →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Form Section */}
      <section id="form" className="form-section">
        <div className="container">
          <div className="form-wrapper">
            <div className="form-content">
              <h2>Get Your Free Savings Estimate</h2>
              <p className="form-subtitle">
                See exactly how much you could save. Free assessment, no obligation.
              </p>
              
              {/* Capacity Progress Bar */}
              <div className="capacity-progress">
                <div className="capacity-header">
                  <span className="capacity-label">This Week's Capacity</span>
                  <span className="capacity-percentage">85%</span>
                </div>
                <div className="progress-bar-container">
                  <div className="progress-bar" style={{ width: '85%' }}>
                    <div className="progress-bar-fill"></div>
                  </div>
                </div>
                <p className="capacity-message">Only a few assessment spots remaining this week!</p>
              </div>
              {!submitted ? (
                <form onSubmit={handleSubmit} className="lead-form">
                  {lastSaved && (
                    <div className="form-save-indicator">
                      <span className="save-icon"><i className="fa-solid fa-floppy-disk"></i></span>
                      <span>Form auto-saved</span>
                    </div>
                  )}
                  <div className="form-group">
                    <label htmlFor="name">Full Name *</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      placeholder="John Smith"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="email">Email Address *</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="phone">Phone Number *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      placeholder="(303) 555-1234"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="address">Property Address *</label>
                    <input
                      type="text"
                      id="address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                      placeholder={`123 Main St, ${locationParam}, CO`}
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="issue">What's your biggest concern? (Optional)</label>
                    <select
                      id="issue"
                      name="issue"
                      value={formData.issue}
                      onChange={handleChange}
                    >
                      <option value="">Select an option</option>
                      <option value="high-bills">High energy bills</option>
                      <option value="cold-rooms">Cold rooms/drafts</option>
                      <option value="hvac-overwork">HVAC running constantly</option>
                      <option value="uncomfortable">Home uncomfortable</option>
                      <option value="rising-costs">Bills keep rising</option>
                      <option value="other">Other</option>
                    </select>
                  </div>
                  <button type="submit" className="btn btn-primary btn-large btn-emotional">
                    Yes! Show Me How Much I Can Save →
                  </button>
                  <p className="form-disclaimer">
                    By submitting, you agree to be contacted about your free savings assessment. 
                    No obligation. We respect your privacy.
                  </p>
                </form>
              ) : (
                <div className="form-success">
                  <div className="success-icon">✓</div>
                  <h3>Thank You!</h3>
                  <p>We've received your request. Our team will contact you within 5 minutes with your personalized savings estimate.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Guarantee Section */}
      <section className="guarantee">
        <div className="container">
          <div className="guarantee-content">
            <div className="guarantee-badge">OUR GUARANTEE</div>
            <h2>Savings Guarantee</h2>
            <p className="guarantee-text">
              We guarantee you'll see measurable savings on your energy bills within 90 days of installation, 
              or we'll work with you until you do, at no additional cost.
            </p>
            <p className="guarantee-disclaimer">*Terms and conditions apply. Availability varies by location.</p>
            <div className="guarantee-features">
              <div className="guarantee-feature">
                <span className="check-icon">✓</span>
                <span>90-day savings guarantee</span>
              </div>
              <div className="guarantee-feature">
                <span className="check-icon">✓</span>
                <span>Free re-assessment if needed</span>
              </div>
              <div className="guarantee-feature">
                <span className="check-icon">✓</span>
                <span>Workmanship warranty included</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Different Section */}
      <section className="different">
        <div className="container">
          <h2 className="section-title">Why Choose Level Up Insulation</h2>
          <div className="different-grid">
            <div className="different-item">
              <h3>Savings-First Approach</h3>
              <p>We focus on ROI, recommending only upgrades that will actually save you money</p>
            </div>
            <div className="different-item">
              <h3>Transparent Pricing</h3>
              <p>Clear cost vs. savings breakdowns so you know exactly what to expect</p>
            </div>
            <div className="different-item">
              <h3>Expert Installation</h3>
              <p>Proper installation is crucial. Bad installation wastes your entire investment</p>
            </div>
            <div className="different-item">
              <h3>Financing Available</h3>
              <p>Monthly payments often less than your current energy waste</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq">
        <div className="container">
          <h2 className="section-title">Frequently Asked Questions</h2>
          <div className="faq-list">
            <div className="faq-item">
              <h3>How much can I really save?</h3>
              <p>Most {locationParam} homeowners save $200-$400/month, depending on home size and current insulation condition. We'll give you an exact estimate.</p>
            </div>
            <div className="faq-item">
              <h3>How long until I see savings?</h3>
              <p>You'll notice lower bills starting with your first month after installation. Full payback typically takes 18-24 months.</p>
            </div>
            <div className="faq-item">
              <h3>Is the assessment really free?</h3>
              <p>Yes, completely free with no obligation. We'll show you exactly where you're losing energy and how much you could save.</p>
            </div>
            <div className="faq-item">
              <h3>What if I can't afford it right now?</h3>
              <p>We offer financing options where your monthly payment is often less than your current energy waste, so you save money from day one.</p>
            </div>
            <div className="faq-item">
              <h3>How long does installation take?</h3>
              <p>Most projects take 1-2 days with minimal disruption. You can stay in your home during installation.</p>
            </div>
            <div className="faq-item">
              <h3>Do you service my area?</h3>
              <p>We service {locationParam} and surrounding suburbs. Enter your address in the form above and we'll confirm availability.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="container">
          <h2 className="section-title">Serving {locationParam} & Surrounding Areas</h2>
          <p className="section-subtitle">
            Helping homeowners across the metro area save money on energy bills
          </p>
          <div className="map-wrapper">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d196281.1291475455!2d-105.1099275!3d39.7392358!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x876b80aa231f17aa%3A0x620436728b3a4514!2sDenver%2C%20CO!5e0!3m2!1sen!2sus!4v1234567890123!5m2!1sen!2sus"
              width="100%"
              height="450"
              style={{ border: 0, borderRadius: '16px' }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Denver Service Area Map"
            ></iframe>
          </div>
          <div className="service-areas">
            <h3>Service Areas</h3>
            <div className="areas-grid">
              <div className="area-item">Denver</div>
              <div className="area-item">Aurora</div>
              <div className="area-item">Lakewood</div>
              <div className="area-item">Westminster</div>
              <div className="area-item">Arvada</div>
              <div className="area-item">Thornton</div>
              <div className="area-item">Centennial</div>
              <div className="area-item">Broomfield</div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section 
        className="final-cta"
        style={{
          backgroundImage: 'url(/images/work-1.jpg)'
        }}
      >
        <div className="final-cta-overlay"></div>
        <div className="container">
          <h2>Every Month You Wait, You're Losing Money</h2>
          <p>Get your free savings assessment today and see exactly how much you could be keeping in your pocket.</p>
          <a href="#form" className="btn btn-primary btn-large btn-emotional">
            Yes! Calculate My Savings Now →
          </a>
          <p className="final-cta-note">Free assessment • No obligation • See your savings estimate today</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <h3>Level Up Insulation</h3>
              <p>Helping {locationParam} homeowners save money</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Services</h4>
                <a href="#form">Energy Savings Assessment</a>
                <a href="#form">Insulation Upgrades</a>
                <a href="#form">Free Consultation</a>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <a href="#form">About Us</a>
                <a href="#form">Contact</a>
                <a href="#form">Privacy Policy</a>
              </div>
              <div className="footer-column">
                <h4>Contact</h4>
                <p><i className="fa-solid fa-phone"></i> <a href="tel:+17204384271">(720) 438-4271</a></p>
                <p><i className="fa-solid fa-envelope"></i> <a href="mailto:info@levelupinsulationco.com">info@levelupinsulationco.com</a></p>
                <p><i className="fa-solid fa-location-dot"></i> 6850 Broadway St, Denver, CO 80221</p>
              </div>
              <div className="footer-column">
                <h4>Hours</h4>
                <p>Mon-Fri: 8:00 AM - 5:00 PM</p>
                <p>Sat: 8:00 AM - 5:00 PM</p>
                <p>Sun: Closed</p>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; {new Date().getFullYear()} Level Up Insulation. All rights reserved.</p>
            <p className="footer-link"><a href="https://levelupinsulationco.com">levelupinsulationco.com</a></p>
          </div>
        </div>
      </footer>

      {/* Floating Phone Button */}
      <a href="tel:+17204384271" className="floating-phone-btn" aria-label="Call us">
        <i className="fa-solid fa-phone"></i>
      </a>
    </div>
  );
}

export default SavingsPage;

